import * as React from "react";
import { useCallback, useState } from "react";
import {
    makeStyles,
    mergeClasses,
    tokens,
    Tree,
    TreeItem,
    TreeItemLayout,
    TreeItemValue,
    TreeOpenChangeData,
    TreeOpenChangeEvent,
} from "@fluentui/react-components";
import { Folder16Regular } from "@fluentui/react-icons";

const TREE_ITEM_LEVEL_TOKEN = "--fluent-TreeItem--level";

const useStyles = makeStyles({
    tree: {
        rowGap: 0,
        width: "100%",
        minWidth: "100%",
        boxSizing: "border-box",
        "& .fui-Tree": {
            width: "100%",
            minWidth: "100%",
            boxSizing: "border-box",
        },
        ":global([role='treeitem']:not([aria-expanded]) > .fui-TreeItemLayout)": {
            paddingLeft: `calc(var(${TREE_ITEM_LEVEL_TOKEN}, 1) * 16px)`,
        },
        ":global([role='treeitem'])": {
            width: "100%",
            minWidth: "100%",
        },
        "& .fui-TreeItemLayout": {
            minHeight: "24px",
            boxSizing: "border-box",
        },
    },
    scrollContainer: {
        width: "100%",
        boxSizing: "border-box",
    },
    treeRoot: {
        width: "max-content",
        minWidth: "100%",
    },
    treeItemLayout: {
        fontSize: "12px",
        cursor: "pointer",
        width: "100%",
        minWidth: "100%",
        boxSizing: "border-box",
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    treeItemLabel: {
        whiteSpace: "nowrap",
        display: "block",
    },
    treeItemLayoutLeaf: {
        minHeight: "24px",
        height: "24px",
    },
    treeItemLayoutSelected: {
        backgroundColor: tokens.colorBrandBackground2,
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    folderIcon: {
        color: tokens.colorNeutralForeground2,
        flexShrink: 0,
    },
});

export interface NewformaFolderTreeNode {
    id: string;
    name: string;
    children?: NewformaFolderTreeNode[];
    defaultExpanded?: boolean;
}

export interface NewformaFolderTreeProps {
    folders: NewformaFolderTreeNode[];
    selectedFolderId?: string;
    onFolderSelected: (folder: NewformaFolderTreeNode) => void;
    ariaLabel?: string;
    dataTestId?: string;
}

const collectDefaultExpandedIds = (folders: NewformaFolderTreeNode[], expandedIds: string[] = []): string[] => {
    for (const folder of folders) {
        if (folder.defaultExpanded) {
            expandedIds.push(folder.id);
        }
        if (folder.children?.length) {
            collectDefaultExpandedIds(folder.children, expandedIds);
        }
    }
    return expandedIds;
};

// Returns the ids of all ancestor folders of the folder with the given id, or undefined when
// the folder is not found. Used to expand the tree down to the current selection on mount.
const collectAncestorIds = (
    folders: NewformaFolderTreeNode[],
    targetId: string,
    ancestorIds: string[] = []
): string[] | undefined => {
    for (const folder of folders) {
        if (folder.id === targetId) {
            return ancestorIds;
        }
        if (folder.children?.length) {
            const result = collectAncestorIds(folder.children, targetId, [...ancestorIds, folder.id]);
            if (result) {
                return result;
            }
        }
    }
    return undefined;
};

export const NewformaFolderTree: React.FC<NewformaFolderTreeProps> = ({
    folders,
    selectedFolderId,
    onFolderSelected,
    ariaLabel,
    dataTestId,
}) => {
    const styles = useStyles();
    const [openItems, setOpenItems] = useState<TreeItemValue[]>(() => {
        const expandedIds = new Set<string>(collectDefaultExpandedIds(folders));
        if (selectedFolderId) {
            for (const ancestorId of collectAncestorIds(folders, selectedFolderId) ?? []) {
                expandedIds.add(ancestorId);
            }
        }
        return Array.from(expandedIds);
    });

    const handleOpenChange = useCallback((event: TreeOpenChangeEvent, data: TreeOpenChangeData) => {
        const eventTarget = event.target as HTMLElement | null;
        const expandIconElement = eventTarget?.closest(".fui-TreeItemLayout__expandIcon");

        if (event.type === "click" && !expandIconElement) {
            return;
        }

        setOpenItems((previousOpenItems) => {
            const nextOpenItems = new Set(previousOpenItems);
            if (data.open) {
                nextOpenItems.add(data.value);
            } else {
                nextOpenItems.delete(data.value);
            }
            return Array.from(nextOpenItems);
        });
    }, []);

    const renderFolder = (folder: NewformaFolderTreeNode, level: number): JSX.Element => {
        const hasChildren = !!folder.children?.length;
        const isSelected = folder.id === selectedFolderId;

        const handleItemClick = (event: React.MouseEvent) => {
            const eventTarget = event.target as HTMLElement | null;
            if (eventTarget?.closest(".fui-TreeItemLayout__expandIcon")) {
                return;
            }

            event.stopPropagation();
            onFolderSelected(folder);
        };

        const layout = (
            <TreeItemLayout
                className={mergeClasses(styles.treeItemLayout, isSelected && styles.treeItemLayoutSelected)}
                style={{ [TREE_ITEM_LEVEL_TOKEN]: level } as React.CSSProperties}
                iconBefore={<Folder16Regular className={styles.folderIcon} />}
            >
                <span className={styles.treeItemLabel}>{folder.name}</span>
            </TreeItemLayout>
        );

        return (
            <TreeItem
                key={folder.id}
                value={folder.id}
                itemType={hasChildren ? "branch" : "leaf"}
                data-testid={`folder-tree-item-${folder.id}`}
                onClick={handleItemClick}
            >
                {layout}
                {hasChildren ? (
                    <Tree className={styles.tree}>
                        {folder.children!.map((child) => renderFolder(child, level + 1))}
                    </Tree>
                ) : (
                    undefined
                )}
            </TreeItem>
        );
    };

    return (
        <Tree
            aria-label={ariaLabel}
            className={mergeClasses(styles.tree, styles.treeRoot)}
            openItems={openItems}
            onOpenChange={handleOpenChange}
            data-testid={dataTestId}
        >
            {folders.map((folder) => renderFolder(folder, 1))}
        </Tree>
    );
};
